// BuyPage — Activate challenge (size + amount)
// Re-skinned from stock-buying: now matches the marketing challenge flow.
//   - Top nav: < Back | "Activate Beginner $50K" | "Market order ⌄"
//   - $79.00 amount (= challenge price)
//   - USD currency selector
//   - "↗ 1 Challenge = $79" / 1 entry
//   - Preset chips: $79 / $149 / $219 / $1000 (MAX)
//   - "Price may change slightly at execution. Capital at risk."

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SIZE_OPTIONS_BY_TIER, TIERS, TIER_ORDER, DEMO_CHALLENGE } from '../../data/platforms';
import { TierPill } from '../../components/Brand';

const PRESETS = [79, 149, 219, 1000];

export function BuyPage() {
  const { stockId } = useParams();
  const nav = useNavigate();
  // stockId is actually the size or "challenge" key
  const userTier = DEMO_CHALLENGE.tier;
  const tier = TIERS[userTier];
  const sizes = SIZE_OPTIONS_BY_TIER[userTier];

  // Default: pick first size option
  const initialSize = stockId && sizes.find(s => s.size === stockId) ? stockId : sizes[0].size;
  const [size, setSize] = useState(initialSize);
  const [amount, setAmount] = useState(sizes.find(s => s.size === initialSize)!.price.toString() + '.00');
  const [currency, setCurrency] = useState('USD');
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');

  const num = parseFloat(amount.replace(/,/g, '')) || 0;
  const sel = sizes.find(s => s.size === size)!;

  // Sync amount when size changes
  useEffect(() => {
    setAmount(sizes.find(s => s.size === size)!.price.toString() + '.00');
  }, [size]);

  function tap(k: string) {
    if (k === '⌫') {
      setAmount((v) => v.replace(/[0-9.]/g, '').slice(0, -1) || '0');
    } else if (k === '.') {
      if (!amount.includes('.')) setAmount((v) => v + '.');
    } else {
      setAmount((v) => (v === '0' ? k : v + k));
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1 text-[15px] font-semibold">
          Activate {userTier} ${size}K
          <button onClick={() => setOrderType((t) => t === 'market' ? 'limit' : 'market')} className="text-tf-text-3">· {orderType === 'market' ? 'Market' : 'Limit'} ⌄</button>
        </div>
        <div className="w-9" />
      </div>

      <div className="px-4 pt-4">
        <div className="rounded-[16px] border-2 border-tf-blue bg-white px-4 pt-4 pb-3">
          <div className="flex items-end justify-between">
            <span className="text-[15px] text-tf-text-3">Pay</span>
            <button onClick={() => setCurrency(currency === 'USD' ? 'EUR' : 'USD')} className="flex items-center gap-1 rounded-full bg-tf-surface-3 px-3 py-1 text-[13px] font-semibold text-tf-text-1">
              $ {currency} <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
          <input
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            data-testid="buy-amount"
            className="mt-1 w-full text-[40px] font-bold tracking-tight text-tf-text-1 outline-none"
            inputMode="decimal"
          />
          <div className="mt-2 flex items-center justify-between text-[13px] text-tf-text-3">
            <span>↗ {userTier} ${size}K = ${sel.price.toFixed(2)}</span>
            <span>≈ 1 challenge</span>
          </div>
        </div>

        {/* Size chips */}
        <div className="mt-3 flex items-center gap-2">
          {sizes.map((s) => (
            <button
              key={s.size}
              onClick={() => setSize(s.size)}
              data-testid={`preset-${s.size}`}
              className={['flex-1 rounded-full border px-3 py-2 text-[14px] font-semibold transition',
                s.size === size ? 'border-tf-text-1 bg-tf-text-1 text-white' : 'border-tf-border bg-white text-tf-text-1'].join(' ')}
            >
              ${s.size}K
            </button>
          ))}
        </div>

        <p className="mt-3 text-[12px] text-tf-text-3">
          Price may change slightly at execution. Capital at risk. No activation fee.
        </p>

        {/* Challenge summary */}
        <div className="mt-4 flex items-center justify-between border-t border-tf-border pt-4">
          <div>
            <div className="text-[15px] font-semibold text-tf-text-1">{userTier} {tier.badge && <span className="ml-1 rounded-full bg-tf-warning px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white align-middle">{tier.badge}</span>}</div>
            <div className="text-[12px] text-tf-text-3">${size}K funded · {tier.profitSplit}% split</div>
          </div>
          <button onClick={() => nav(-1)} className="text-[14px] font-semibold text-tf-blue">Change</button>
        </div>
      </div>

      <div className="px-4 pt-4">
        <button
          onClick={() => nav('/portal/review', { state: { challenge: { tier: userTier, size, platform: 'Deepchart' }, amount: num, shares: 1 } })}
          data-testid="buy-continue"
          className="flex h-[52px] w-full items-center justify-center rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
        >
          Continue
        </button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-px bg-tf-surface-3 px-px pb-px">
        {['1','2','3','4','5','6','7','8','9','.','0','⌫'].map((k) => (
          <button
            key={k}
            onClick={() => tap(k)}
            data-testid={`key-${k === '⌫' ? 'back' : k}`}
            className="flex h-14 items-center justify-center bg-white text-[24px] font-medium text-tf-text-1 active:bg-tf-surface-1"
          >
            {k}
          </button>
        ))}
      </div>
    </div>
  );
}
